/**
 * Unified Projects Component
 * Consolidates all project display functionality across portfolio, company, activity, and skill pages
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { List, BarChart3, Briefcase, User, Calendar } from 'lucide-react';
import { ProjectCard } from './ProjectCard';
import { TimelineView } from './TimelineView';
import { useProjectFiltering } from './useProjectFiltering';
import { useSkillsState } from '@/lib/skills';
import { UnifiedProjectsProps, ViewMode, TimeScale } from './types';

export const UnifiedProjectsComponent: React.FC<UnifiedProjectsProps> = ({
  activityId,
  companyName,
  skillId,
  requiredSkills,
  showPrivateProjects: enablePrivateProjects = true,
  showCompanyLogos = true,
  defaultViewMode = 'list',
  pageTitle,
  emptyStateMessage = "No projects found matching the criteria.",
  onPersonalToggle,
  showPersonal,
  onBadgeSelectionChange
}) => {
  // Use centralized skill state management
  const { badgeSelection, updateBadgeSelection } = useSkillsState();
  
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [internalShowPrivate, setInternalShowPrivate] = useState(enablePrivateProjects);
  
  // Use external state if provided, otherwise use centralized state
  const showPrivate = showPersonal !== undefined ? showPersonal : internalShowPrivate;
  const showProfessional = badgeSelection.showProfessional;
  const showPrivateProjects = badgeSelection.showPrivate;
  
  const handlePrivateToggle = (value: boolean) => {
    if (onPersonalToggle) {
      onPersonalToggle(value);
    } else {
      setInternalShowPrivate(value);
    }
  };

  // Calculate which projects to show based on badge selections
  const effectiveShowPrivate = useMemo(() => {
    if (showProfessional && showPrivateProjects) {
      return true; // Show all projects (both professional and private)
    } else if (showPrivateProjects && !showProfessional) {
      return 'only'; // Show only private projects
    } else if (showProfessional && !showPrivateProjects) {
      return false; // Show only professional projects
    } else {
      return true; // Neither selected, show all as fallback
    }
  }, [showProfessional, showPrivateProjects]);
  const [timeScale, setTimeScale] = useState<TimeScale>('quarter');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [containerWidth, setContainerWidth] = useState(800);
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  // Use custom hook for project filtering
  const { sortedProjects, professionalProjects, privateProjects, totalProjects } = useProjectFiltering({
    activityId,
    companyName,
    skillId,
    requiredSkills,
    showPrivate: effectiveShowPrivate
  });

  // Update container width when component mounts and window resizes
  useEffect(() => {
    const updateContainerWidth = () => {
      if (viewMode === 'timeline') {
        // For timeline view, use full viewport width
        setContainerWidth(window.innerWidth - 48); // Account for 24px padding on each side
      } else if (timelineContainerRef.current) {
        // For list view, use actual container width
        const width = timelineContainerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, [viewMode]);

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {pageTitle && (
            <h2 className="text-2xl font-bold tracking-tight">{pageTitle}</h2>
          )}
          
          <div className="flex items-center gap-2">
            {/* Professional Projects Badge - Always visible and clickable */}
            <Badge 
              variant={showProfessional ? "default" : "outline"} 
              className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                const newShowProfessional = !showProfessional;
                updateBadgeSelection({
                  showProfessional: newShowProfessional,
                  showPrivate: showPrivateProjects,
                });
                onBadgeSelectionChange?.(newShowProfessional, showPrivateProjects);
              }}
            >
              <Briefcase className="w-3 h-3" />
              {professionalProjects.length}
            </Badge>
            
            {/* Private Projects Badge - Always visible and clickable */}
            <Badge 
              variant={showPrivateProjects ? "default" : "outline"} 
              className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                const newShowPrivateProjects = !showPrivateProjects;
                updateBadgeSelection({
                  showProfessional: showProfessional,
                  showPrivate: newShowPrivateProjects,
                });
                onBadgeSelectionChange?.(showProfessional, newShowPrivateProjects);
              }}
            >
              <User className="w-3 h-3" />
              {privateProjects.length}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Timeline controls */}
          {viewMode === 'timeline' && (
            <>
              <Select value={timeScale} onValueChange={(value: TimeScale) => setTimeScale(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Months</SelectItem>
                  <SelectItem value="quarter">Quarters</SelectItem>
                  <SelectItem value="year">Years</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                  disabled={zoomLevel <= 0.5}
                >
                  -
                </Button>
                <span className="text-sm text-muted-foreground min-w-[4rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                  disabled={zoomLevel >= 3}
                >
                  +
                </Button>
              </div>
            </>
          )}

          {/* View mode toggle */}
          <div className="flex items-center border rounded-lg">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-r-none"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('timeline')}
              className="rounded-l-none"
            >
              <BarChart3 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Projects display */}
      <AnimatePresence mode="wait">
        {totalProjects === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-12 text-muted-foreground"
          >
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{emptyStateMessage}</p>
          </motion.div>
        ) : viewMode === 'list' ? (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {sortedProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                showCompanyLogos={showCompanyLogos}
                index={index}
              />
            ))}
          </motion.div>
        ) : (
          <TimelineView
            sortedProjects={sortedProjects}
            timeScale={timeScale}
            zoomLevel={zoomLevel}
            containerWidth={containerWidth}
            showCompanyLogos={showCompanyLogos}
          />
        )}
      </AnimatePresence>
    </div>
  );
};